import { type EnqueueSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";

interface UseFormLockingProps {
  initialLockState: boolean;
  isSaving: boolean;
  enqueueSnackbar: EnqueueSnackbar;
}

/**
 * Hook for managing submission lock state and warnings
 */
export const useFormLocking = ({
  initialLockState,
  isSaving,
  enqueueSnackbar,
}: UseFormLockingProps) => {
  const [isFormLocked, setIsFormLocked] = useState(initialLockState);

  const savingSnackbarShownRef = useRef(false);
  const lockedSnackbarShownRef = useRef(false);

  const showLockWarning = useCallback(() => {
    if (isFormLocked) {
      if (!lockedSnackbarShownRef.current) {
        enqueueSnackbar(
          "This submission has been locked. Please unlock to enable editing.",
          {
            variant: "warning",
            anchorOrigin: { horizontal: "right", vertical: "bottom" },
          },
        );
        lockedSnackbarShownRef.current = true;
      }
    }
  }, [isFormLocked, enqueueSnackbar]);

  useEffect(() => {
    if (!isSaving) {
      savingSnackbarShownRef.current = false;
    }
    if (!isFormLocked) {
      lockedSnackbarShownRef.current = false;
    }
  }, [isSaving, isFormLocked]);

  return {
    isFormLocked,
    setIsFormLocked,
    showLockWarning,
  };
};
